import React, { useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { APP_NAME, META_DESCRIPTION } from './const'
import { config } from './config'

type Props = {
  title?: string
  description?: string
  // canonical URL のパス(クエリ含む)。省略時は現在の pathname を使う
  path?: string
  // true のとき robots noindex を出力し、canonical は出力しない
  noindex?: boolean
}

const updateMetaContent = (selector: string, content: string): void => {
  const element = document.querySelector(selector)
  if (element !== null) {
    element.setAttribute('content', content)
  }
}

const updateCanonicalLink = (href: string): void => {
  let link = document.querySelector('link[rel="canonical"]')
  if (link === null) {
    link = document.createElement('link')
    link.setAttribute('rel', 'canonical')
    document.head.appendChild(link)
  }
  link.setAttribute('href', href)
}

const removeCanonicalLink = (): void => {
  document.querySelector('link[rel="canonical"]')?.remove()
}

// SPA 遷移で前ページの noindex が残らないよう、ページごとに付け外しする
const updateRobotsNoindex = (noindex: boolean): void => {
  let robots = document.querySelector('meta[name="robots"]')
  if (!noindex) {
    robots?.remove()
    return
  }
  if (robots === null) {
    robots = document.createElement('meta')
    robots.setAttribute('name', 'robots')
    document.head.appendChild(robots)
  }
  // 既存の robots meta(index,follow 等)があっても noindex で上書きする
  robots.setAttribute('content', 'noindex')
}

export const Meta: React.FC<Props> = ({ title, description, path, noindex = false }) => {
  const metaTitle = title ? `${title} | ${APP_NAME}` : APP_NAME
  const metaDescription = description ?? META_DESCRIPTION
  // title が変わらない遷移(同一クリエイターの別商品など)でも canonical を更新するため、pathname の変化を検知する
  const { pathname } = useLocation()

  const setMetaTags = useCallback(() => {
    document.title = metaTitle
    updateMetaContent('meta[name="description"]', metaDescription)
    updateMetaContent('meta[property="og:title"]', metaTitle)
    updateMetaContent('meta[property="og:description"]', metaDescription)
    updateMetaContent('meta[name="twitter:title"]', metaTitle)
    updateMetaContent('meta[name="twitter:description"]', metaDescription)

    updateRobotsNoindex(noindex)

    // 正規 URL はアクセス元ホストではなく設定上のユーザーサイト URL を基準にする。
    // creator / business サイトのパスはユーザーサイトに存在しないため canonical は出力しない
    const isUserSite = document.domain === config.userFrontDomain
    const canonicalUrl = `${isUserSite ? config.userFrontEndpoint : window.location.origin}${path ?? pathname}`
    if (noindex || !isUserSite) {
      removeCanonicalLink()
    } else {
      updateCanonicalLink(canonicalUrl)
    }
    updateMetaContent('meta[property="og:url"]', canonicalUrl)
    updateMetaContent('meta[name="twitter:url"]', canonicalUrl)
  }, [metaTitle, metaDescription, path, pathname, noindex])

  useEffect(() => {
    setMetaTags()
  }, [setMetaTags])

  return null
}
